import dayjs from 'dayjs';
import type { TimeSeriesPoint } from 'api/new-analytics/types';

const DAY_TIER_MAX_DAYS = 14;
const WEEK_TIER_MAX_DAYS = 92;
const MONDAY = 1;

function rangeSpanInDays(dates: string[]): number {
  return dayjs(dates[dates.length - 1]).diff(dayjs(dates[0]), 'day');
}

/**
 * Builds x-axis labels for a daily time series, adapting label density to the
 * selected range so the axis stays readable while the line keeps its daily
 * points. Non-labelled points are blank (`''`):
 * - up to 14 days: every day is labelled (`MMM D`)
 * - up to ~3 months: only Mondays are labelled (`MMM D`)
 * - longer: only the first day of each month is labelled (`MMM`)
 */
export function buildTimeAxisLabels(dates: string[]): string[] {
  if (dates.length === 0) {
    return [];
  }
  const spanInDays = rangeSpanInDays(dates);

  if (spanInDays <= DAY_TIER_MAX_DAYS) {
    return dates.map((date) => dayjs(date).format('MMM D'));
  }

  if (spanInDays <= WEEK_TIER_MAX_DAYS) {
    return dates.map((date) => {
      const parsed = dayjs(date);
      return parsed.day() === MONDAY ? parsed.format('MMM D') : '';
    });
  }

  let previousMonthKey = '';
  return dates.map((date) => {
    const parsed = dayjs(date);
    const monthKey = parsed.format('YYYY-MM');
    if (monthKey === previousMonthKey) {
      return '';
    }
    previousMonthKey = monthKey;
    return parsed.format('MMM');
  });
}

/**
 * Full per-day date titles (`MMM D`) for chart tooltips, so hovering any day —
 * including days whose axis label is blanked by {@link buildTimeAxisLabels} —
 * shows a real date.
 */
export function buildTimeAxisTooltipTitles(dates: string[]): string[] {
  return dates.map((date) => dayjs(date).format('MMM D'));
}

/**
 * Convenience for time-series charts: returns the adaptive axis `labels` and the
 * full-date `tooltipTitles` for the same daily `dates`, so a chart can spread
 * both onto its chart-data object.
 */
export function buildTimeAxis(dates: string[]): {
  labels: string[];
  tooltipTitles: string[];
} {
  return {
    labels: buildTimeAxisLabels(dates),
    tooltipTitles: buildTimeAxisTooltipTitles(dates),
  };
}

export function collectSortedDates(
  series: (TimeSeriesPoint[] | undefined)[],
): string[] {
  const set = new Set<string>();
  series.forEach((maybeSeries) =>
    maybeSeries?.forEach((point) => set.add(point.date)),
  );
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function valuesForDates(
  points: TimeSeriesPoint[] | undefined,
  dates: string[],
): number[] {
  if (!points?.length) {
    return dates.map(() => 0);
  }
  const map = new Map(points.map((point) => [point.date, point.value]));
  return dates.map((date) => map.get(date) ?? 0);
}
